import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ConvexProviderWithAuth, ConvexReactClient } from 'convex/react';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';

interface ConvexRealtimeProviderProps {
  children: React.ReactNode;
}

interface ConvexRealtimeStatus {
  isConnected: boolean;
}

const ConvexRealtimeStatusContext = createContext<ConvexRealtimeStatus>({ isConnected: false });

export function ConvexRealtimeProvider({ children }: ConvexRealtimeProviderProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const convexUrl = process.env.REACT_APP_CONVEX_URL || '';
  const [isConnected, setIsConnected] = useState(false);
  const tokenCacheRef = useRef<{ token: string; expiresAt: number } | null>(null);

  const convex = useMemo(() => (convexUrl ? new ConvexReactClient(convexUrl) : null), [convexUrl]);

  useEffect(() => {
    if (!isAuthenticated) {
      tokenCacheRef.current = null;
      setIsConnected(false);
    }
  }, [isAuthenticated]);

  const fetchAccessToken = useCallback(
    async ({ forceRefreshToken }: { forceRefreshToken: boolean }): Promise<string | null> => {
      const userToken = sessionStorage.getItem('accessToken');
      if (!userToken) {
        tokenCacheRef.current = null;
        setIsConnected(false);
        return null;
      }

      if (forceRefreshToken) {
        tokenCacheRef.current = null;
      }

      const now = Math.floor(Date.now() / 1000);
      const cached = tokenCacheRef.current;
      if (!forceRefreshToken && cached && now < cached.expiresAt - 60) {
        return cached.token;
      }

      try {
        const response = await apiService.getConvexToken();
        if (!response?.success || !response.token) {
          setIsConnected(false);
          return null;
        }

        tokenCacheRef.current = { token: response.token, expiresAt: response.expiresAt };
        setIsConnected(true);
        return response.token;
      } catch {
        tokenCacheRef.current = null;
        setIsConnected(false);
        return null;
      }
    },
    [isAuthenticated], // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (!convexUrl || !convex) {
    return (
      <ConvexRealtimeStatusContext.Provider value={{ isConnected: false }}>
        {children}
      </ConvexRealtimeStatusContext.Provider>
    );
  }

  return (
    <ConvexRealtimeStatusContext.Provider value={{ isConnected }}>
      <ConvexProviderWithAuth
        client={convex}
        useAuth={() => ({
          isLoading,
          isAuthenticated,
          fetchAccessToken,
        })}
      >
        {children}
      </ConvexProviderWithAuth>
    </ConvexRealtimeStatusContext.Provider>
  );
}

export function useConvexRealtimeStatus() {
  return useContext(ConvexRealtimeStatusContext);
}
